import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useLocation, useNavigate } from 'react-router-dom'
import { useExtranetLinks } from '../../components/navigation/useExtranetLinks'
import { LanguageContext } from './languageContext'
import * as ContextUtils from './languageContextUtils'

export const LanguageProvider = (props: PropsWithChildren) => {
  const extranetLinks = useExtranetLinks()

  const { i18n } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()

  const [previousLanguage, setPreviousLanguage] = useState<string>(i18n.language || 'fi')

  const isActiveLanguage = useCallback(
    (language: string) => {
      if (ContextUtils.isValidLanguage(language)) {
        return language === i18n.language
      }

      return false
    },
    [i18n.language]
  )

  const changeLanguage = useCallback(
    (language: string) => {
      if (!ContextUtils.isValidLanguage(language) || language === i18n.language) return

      i18n.changeLanguage(language)
      localStorage.setItem('language', language)
    },
    [i18n.language]
  )

  useEffect(() => {
    if (i18n.language !== previousLanguage) {
      const matchingRoutes = ContextUtils.matchExtranetRoutes(previousLanguage, location)
      const matchingRoute = ContextUtils.matchExtranetRoute(matchingRoutes)

      const nextRoute = ContextUtils.findMatchForNextLanguage(
        matchingRoute,
        extranetLinks,
        i18n.language
      )

      const defaultPath = i18n.language === 'fi' ? '/' : '/' + i18n.language

      const navigateTo =
        !!nextRoute && !!nextRoute.path
          ? generatePath(nextRoute.path, matchingRoute.params)
          : defaultPath

      setPreviousLanguage(i18n.language)
      navigate(navigateTo)
    }
  }, [i18n.language, previousLanguage])

  return (
    <LanguageContext.Provider value={{ changeLanguage, isActiveLanguage }}>
      {props.children}
    </LanguageContext.Provider>
  )
}
